'use client';

import {
  createPdfPagePreviewUrl,
  createPdfPreviewUrl,
  isPdfFile,
  openPdfDocument,
  type PdfJsDocument,
} from '@/lib/browser-pdf-tools';

export type PdfToolSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
  previewUrl: string | null;
};

export type PdfToolPagePreview = {
  id: string;
  pageNumber: number;
  previewUrl: string;
};

export async function readPdfSource(file: File, password?: string): Promise<PdfToolSource> {
  const document = await openPdfDocument(new Uint8Array(await file.arrayBuffer()), password);

  try {
    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: document.numPages,
      previewUrl: await createPdfPreviewUrl(document),
    };
  } catch {
    throw new Error(
      `"${file.name}" could not be opened. Use a standard PDF file and check the password if it is locked.`,
    );
  } finally {
    document.cleanup?.();
    await document.destroy?.();
  }
}

export async function readPdfPagePreviews(
  file: File,
  password?: string,
): Promise<{
  source: PdfToolSource;
  pages: PdfToolPagePreview[];
}> {
  const document = await openPdfDocument(new Uint8Array(await file.arrayBuffer()), password);

  try {
    const pages: PdfToolPagePreview[] = [];

    for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
      const page = await document.getPage(pageNumber);

      try {
        pages.push({
          id: crypto.randomUUID(),
          pageNumber,
          previewUrl: await createPdfPagePreviewUrl(page),
        });
      } finally {
        page.cleanup?.();
      }
    }

    return {
      source: {
        id: crypto.randomUUID(),
        file,
        name: file.name,
        size: file.size,
        pageCount: document.numPages,
        previewUrl: pages[0]?.previewUrl ?? (await createPdfPreviewUrl(document)),
      },
      pages,
    };
  } catch {
    throw new Error(
      `"${file.name}" could not be opened. Use a standard PDF file and check the password if it is locked.`,
    );
  } finally {
    document.cleanup?.();
    await document.destroy?.();
  }
}

export function revokePdfSource(source: PdfToolSource | null) {
  if (source?.previewUrl) {
    URL.revokeObjectURL(source.previewUrl);
  }
}

export function revokePdfPagePreviews(pages: PdfToolPagePreview[]) {
  pages.forEach((page) => URL.revokeObjectURL(page.previewUrl));
}

export function buildPdfAcceptError(file: File | null, fallback: string) {
  if (!file) {
    return fallback;
  }

  return isPdfFile(file) ? null : fallback;
}

export async function withPdfDocument<T>(
  file: File,
  password: string | undefined,
  callback: (document: PdfJsDocument) => Promise<T>,
) {
  const document = await openPdfDocument(new Uint8Array(await file.arrayBuffer()), password);

  try {
    return await callback(document);
  } finally {
    document.cleanup?.();
    await document.destroy?.();
  }
}

export function formatPdfPageLabel(pageCount: number) {
  return pageCount === 1 ? '1 page' : `${pageCount} pages`;
}

export function arrayBufferToUint8(file: File) {
  return file.arrayBuffer().then((buffer) => new Uint8Array(buffer));
}
